import { Collapse } from "antd";
import { useNavigate } from "react-router-dom";
import S from "./NicheLanding.styles";
import { NicheConfig } from "./types";

interface Props {
  config: NicheConfig;
}

export const NicheLanding: React.FC<Props> = ({ config }) => {
  const navigate = useNavigate();

  const handleHref = (href: string) => {
    if (href.startsWith("http")) {
      window.open(href, "_blank", "noopener,noreferrer");
    } else {
      navigate(href);
    }
  };

  return (
    <S.PageWrapper>
      {/* HERO */}
      <S.HeroSection>
        <S.Eyebrow>{config.hero.eyebrow}</S.Eyebrow>
        <S.HeroTitle>{config.hero.title}</S.HeroTitle>
        <S.HeroSubtitle>{config.hero.subtitle}</S.HeroSubtitle>
        <S.HeroCtaRow>
          <S.PrimaryCta
            type="primary"
            onClick={() => handleHref(config.hero.primaryCtaHref)}
          >
            {config.hero.primaryCta}
          </S.PrimaryCta>
          {config.hero.secondaryCta && config.hero.secondaryCtaHref && (
            <S.SecondaryCta
              onClick={() => handleHref(config.hero.secondaryCtaHref!)}
            >
              {config.hero.secondaryCta}
            </S.SecondaryCta>
          )}
        </S.HeroCtaRow>
      </S.HeroSection>

      {/* INTRO TEXT (SEO body content) */}
      <S.IntroSection>
        <S.IntroHeading>{config.intro.heading}</S.IntroHeading>
        {config.intro.paragraphs.map((p, i) => (
          <S.IntroParagraph key={i}>{p}</S.IntroParagraph>
        ))}
      </S.IntroSection>

      <S.Divider />

      {/* PAIN POINTS */}
      <S.SectionHeading>{config.pain.heading}</S.SectionHeading>
      <S.PainGrid>
        {config.pain.items.map((p, i) => (
          <S.PainCard key={i}>
            <S.PainEmoji>{p.emoji}</S.PainEmoji>
            <S.PainTitle>{p.title}</S.PainTitle>
            <S.PainText>{p.text}</S.PainText>
          </S.PainCard>
        ))}
      </S.PainGrid>

      <S.Divider />

      {/* FEATURES */}
      <S.SectionHeading>{config.features.heading}</S.SectionHeading>
      <S.FeatureGrid>
        {config.features.items.map((f, i) => (
          <S.FeatureCard key={i}>
            <S.FeatureCheck>✓</S.FeatureCheck>
            <S.FeatureBody>
              <S.FeatureTitle>{f.title}</S.FeatureTitle>
              <S.FeatureText>{f.text}</S.FeatureText>
            </S.FeatureBody>
          </S.FeatureCard>
        ))}
      </S.FeatureGrid>

      {/* TESTIMONIALS (optional) */}
      {config.testimonials && config.testimonials.items.length > 0 && (
        <>
          <S.Divider />
          <S.SectionHeading>{config.testimonials.heading}</S.SectionHeading>
          <S.TestimonialGrid>
            {config.testimonials.items.map((t, i) => (
              <S.TestimonialCard key={i}>
                <S.TestimonialQuote>{t.quote}</S.TestimonialQuote>
                <S.TestimonialAttr>{t.name}</S.TestimonialAttr>
                <S.TestimonialMeta>
                  {t.business}
                  {t.city ? `, ${t.city}` : ""}
                </S.TestimonialMeta>
              </S.TestimonialCard>
            ))}
          </S.TestimonialGrid>
        </>
      )}

      <S.Divider />

      {/* FAQ */}
      <S.SectionHeading>{config.faq.heading}</S.SectionHeading>
      <S.FaqWrapper>
        <Collapse
          bordered={false}
          expandIconPosition="end"
          items={config.faq.items.map((item, i) => ({
            key: String(i),
            label: item.question,
            children: <p>{item.answer}</p>,
          }))}
        />
      </S.FaqWrapper>

      {/* CTA BLOCK */}
      <S.CtaBlock>
        <S.CtaTitle>{config.cta.heading}</S.CtaTitle>
        <S.CtaBody>{config.cta.body}</S.CtaBody>
        <S.PrimaryCta
          type="primary"
          onClick={() => handleHref(config.cta.buttonHref)}
        >
          {config.cta.buttonText}
        </S.PrimaryCta>
      </S.CtaBlock>
    </S.PageWrapper>
  );
};
